import { EnforcementIntentDetail } from "common/graphql";
import { enforcementIntentSimpleStatus } from "common/graphql/fragments/enforcementIntentSummary";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import React from "react";
import NoticeReviewActions from "./NoticeReviewActions";
import NoticePostIssueActions from "./NoticePostIssueActions";
import LoadingSkeleton from "@app.automotus.io/components/LoadingSkeleton";
import IconButton from "@mui/material/IconButton";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import UpdateNoticeNumberDialog from "./UpdateNoticeNumberDialog";
import Grid from "@mui/material/Grid";
import useMediaQuery from "@mui/material/useMediaQuery";
import type { Theme } from "@mui/material/styles";
import { sanitizeInternalPath } from "common/helpers";

export const NoticeActionsHeader: React.FC<NoticeActionsHeaderProps> = ({
  intent,
  loading,
  isReviewEnabled,
  refetch,
  selectedEnforcementPolicyId,
  selectedOperatorId,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = React.useState(false);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  const from = (location.state as { from?: string })?.from;
  // Only ever set on a link opened in a new tab — `state` doesn't survive that, so this is the
  // fallback for restoring the exact list view when `from` (below) isn't available. Sanitized
  // since it's user-controllable (a crafted link's query param) and gets passed to navigate().
  const returnTo = sanitizeInternalPath(searchParams.get("returnTo"));

  const handleClickBack = () => {
    // `from` is only ever present for a same-tab navigation (it lives in in-memory history
    // state), so when it's there, prefer `navigate(-1)` — a real history pop, which correctly
    // preserves the browser's forward-navigation stack instead of pushing a duplicate entry.
    if (from === "notice-summary") {
      navigate(-1);
      return;
    }
    if (returnTo) {
      navigate(returnTo);
      return;
    }
    navigate("..");
  };

  const isPendingReview = intent?.reviews.some((review) => review.status === "pending");

  const simpleStatus = intent ? enforcementIntentSimpleStatus[intent.status] : undefined;
  const isIssued = simpleStatus === "issued";
  const showVoidButton = Boolean(
    intent &&
      !intent.voidedAt &&
      isIssued &&
      !isPendingReview &&
      intent.status !== "paid" &&
      intent.policy.workflowIntegrationConfig?.isVoidSubmittedEnforcementIntentAllowed,
  );
  const showRecordPaymentButton = Boolean(
    intent &&
      !intent.voidedAt &&
      isIssued &&
      !isPendingReview &&
      intent.status !== "paid" &&
      intent.policy.workflowIntegrationConfig?.isApplyPaymentToEnforcementIntentAllowed,
  );

  // Notice number can only be edited if the workflow config allows it, and it has not already been edited.
  const isNoticeNumberEditEnabled =
    isReviewEnabled && isPendingReview && intent?.policy.workflowIntegrationConfig?.officerInputNoticeNumberRegex;

  const isIssueEnabled = Boolean(
    isReviewEnabled &&
      intent &&
      isPendingReview &&
      (!intent.policy.workflowIntegrationConfig?.officerInputNoticeNumberRegex || intent.officerInputNoticeNumber) &&
      (!intent.policy.workflowIntegrationConfig?.isDueDateRequired || intent.dueAt),
  );

  const requiresPolicySelection = intent?.policy.routingParameter === "user_input";

  return (
    <Grid container rowSpacing={1}>
      <Grid item xs={12} sm={6}>
        <Box display="flex" alignItems="center" justifyContent="flex-start">
          <IconButton onClick={handleClickBack}>
            <ArrowBackIosIcon />
          </IconButton>
          <LoadingSkeleton loading={loading}>
            <Typography variant="h5">
              {isMobile ? "" : "Notice Number:"} {intent?.noticeNumber || ""}
            </Typography>
            {intent && isNoticeNumberEditEnabled && (
              <>
                <IconButton size="small" onClick={() => setIsUpdateDialogOpen(true)}>
                  <EditIcon sx={{ fontSize: "16px" }} />
                </IconButton>
                <UpdateNoticeNumberDialog
                  open={isUpdateDialogOpen}
                  onClose={() => setIsUpdateDialogOpen(false)}
                  enforcementIntentId={intent.id}
                  noticeNumberRegex={intent.policy.workflowIntegrationConfig?.officerInputNoticeNumberRegex || ""}
                  noticeNumberPrefixRegex={
                    intent.policy.workflowIntegrationConfig?.officerInputNoticeNumberPrefixRegex || ""
                  }
                />
              </>
            )}
          </LoadingSkeleton>
        </Box>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Box>
          {intent && intent.status === "requires_officer_review" && (
            <NoticeReviewActions
              isIssueEnabled={isIssueEnabled}
              isVoidEnabled={!!isReviewEnabled}
              requiresPolicySelection={requiresPolicySelection}
              selectedEnforcementPolicyId={selectedEnforcementPolicyId ?? null}
              selectedOperatorId={selectedOperatorId ?? null}
              intent={intent}
            />
          )}
          {intent && (showVoidButton || showRecordPaymentButton) && refetch && (
            <NoticePostIssueActions
              id={intent.id}
              showVoidButton={showVoidButton}
              showRecordPaymentButton={showRecordPaymentButton}
              refetch={refetch}
            />
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export interface NoticeActionsHeaderProps {
  intent?: EnforcementIntentDetail;
  loading: boolean;
  isReviewEnabled?: boolean;
  refetch?: () => void;
  selectedEnforcementPolicyId?: string | null;
  /** Fleet operator selected for one-time assignment on issue (AUT-5032) */
  selectedOperatorId?: string | null;
}

export default NoticeActionsHeader;
